import { FC } from 'react';
import {
  CaretRightIcon,
  DotsSixVerticalIcon,
  LockSimpleIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
} from '@phosphor-icons/react';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './CategoryRow.module.scss';

interface ICategoryRowProps {
  label: string;
  /** `true` per una macro: testo più marcato e riga più alta. */
  isMacro: boolean;
  /**
   * Di sistema (lucchetto) o della società (omino). È la distinzione che
   * spiega perché su alcune righe mancano rinomina ed elimina: senza icona
   * l'assenza delle azioni sembrava un difetto, non una regola.
   */
  isSystem: boolean;
  /**
   * Rinomina ed elimina: solo sulle voci proprie.
   *
   * Separato da `onAddChild` di proposito. Quando le tre azioni stavano dietro
   * un unico flag, il «+» spariva su tutte le macro di sistema — cioè su tutte
   * e dodici — e le sottocategorie proprie diventavano impossibili da creare.
   */
  canModify: boolean;
  /** Quante sotto contiene, per la pastiglia sulla testata. */
  childCount?: number;
  isOpen?: boolean;
  /** Assente se la macro non ha sotto: non c'è niente da aprire. */
  onToggleOpen?: () => void;
  /**
   * Maniglia di trascinamento: listener e attributi di dnd-kit.
   *
   * Il riordino vale anche sulle voci di SISTEMA: non le modifica, cambia solo
   * l'ordine in cui questa società le vede (ADR 027). Per questo la maniglia
   * non dipende da `canModify`.
   */
  dragHandle?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  /** Presente su ogni macro: crea una sotto dentro questa, di sistema o no. */
  onAddChild?: () => void;
  isPendingDelete: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

/**
 * Una voce dell'elenco.
 *
 * La forma è quella delle righe-scheda già usate altrove nel progetto
 * (`TestConfigItem`): riquadro con angoli smussati, fondo al passaggio del
 * mouse, azioni a destra. Prima erano righe nude e l'elenco sembrava un
 * elenco puntato, non una lista di cose su cui si agisce.
 *
 * Rinomina, elimina e frecce compaiono in hover: su un elenco lungo mostrarle
 * sempre riempirebbe la colonna destra di rumore uguale. Il «+» fa eccezione
 * ed è sempre in vista, perché è l'unico modo di creare una sottocategoria e,
 * nascosto, la funzione risultava inesistente.
 */
const CategoryRow: FC<ICategoryRowProps> = ({
  label,
  isMacro,
  isSystem,
  canModify,
  childCount = 0,
  isOpen = false,
  onToggleOpen,
  dragHandle,
  isDragging = false,
  onAddChild,
  isPendingDelete,
  isSaving,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}) => {
  const rowClass = [styles.row, isMacro && styles.macro, isDragging && styles.dragging]
    .filter(Boolean)
    .join(' ');

  if (isPendingDelete) {
    return (
      <div className={`${rowClass} ${styles.confirming}`}>
        <Typhography size="medium-12" className={styles.confirmText}>
          Eliminare «{label}»?
        </Typhography>
        <span className={styles.actions}>
          <CustomButton size="xs" color="outline" onClick={onCancelDelete}>
            Annulla
          </CustomButton>
          <CustomButton
            size="xs"
            color="error"
            isLoading={isSaving}
            onClick={onConfirmDelete}
          >
            Elimina
          </CustomButton>
        </span>
      </div>
    );
  }

  const Head = onToggleOpen ? 'button' : 'div';

  return (
    <div className={rowClass}>
      {/* La maniglia PRIMA di tutto: è il punto da cui si afferra, e va
          trovata sempre nello stesso posto. Due frecce su/giù chiedevano un
          clic per posizione — trascinare dice dove va la riga in un gesto. */}
      <span
        className={styles.handle}
        aria-label={`Trascina ${label} per riordinare`}
        {...(dragHandle ?? {})}
      >
        <DotsSixVerticalIcon size={14} />
      </span>

      <Head
        className={styles.head}
        {...(onToggleOpen
          ? {
              type: 'button' as const,
              onClick: onToggleOpen,
              'aria-expanded': isOpen,
            }
          : {})}
      >
        {/* Lo spazio del caret è riservato anche a chi non ce l'ha, altrimenti
            le macro senza sotto partirebbero da una x diversa dalle altre. */}
        <span className={styles.caret} aria-hidden>
          {onToggleOpen && (
            <CaretRightIcon
              size={12}
              className={isOpen ? styles.caretOpen : undefined}
            />
          )}
        </span>

        <span
          className={styles.origin}
          title={isSystem ? 'Categoria di sistema' : 'Categoria della società'}
        >
          {isSystem ? <LockSimpleIcon size={13} /> : <UserIcon size={13} />}
        </span>

        <Typhography
          size={isMacro ? 'medium-14' : 'regular-12'}
          className={styles.label}
        >
          {label}
        </Typhography>

        {/* Dice che dentro c'è qualcosa senza costringere ad aprire. */}
        {childCount > 0 && !isOpen && (
          <span className={styles.count}>
            <Typhography size="medium-12" className={styles.countText}>
              {childCount}
            </Typhography>
          </span>
        )}
      </Head>

      <span className={styles.actions}>
        {canModify && (
          <CustomButton
            size="xs"
            color="text"
            ariaLabel={`Modifica ${label}`}
            icon={<PencilSimpleIcon size={14} />}
            iconPosition="left"
            onClick={onEdit}
          />
        )}
        {canModify && (
          <CustomButton
            size="xs"
            color="text"
            ariaLabel={`Elimina ${label}`}
            icon={<TrashIcon size={14} />}
            iconPosition="left"
            onClick={onAskDelete}
          />
        )}
      </span>

      {/* Lo slot esiste anche sulle righe che non ce l'hanno (le sotto, che non
          possono contenere nulla): senza, le loro frecce cadrebbero su una x
          diversa da quelle delle macro e la colonna destra risulterebbe
          sfrangiata. */}
      <span className={styles.addAction}>
        {onAddChild && (
          <CustomButton
            size="xs"
            color="text"
            ariaLabel={`Aggiungi una sottocategoria dentro ${label}`}
            icon={<PlusIcon size={14} />}
            iconPosition="left"
            onClick={onAddChild}
          />
        )}
      </span>
    </div>
  );
};

export default CategoryRow;
